import React, { FC, useRef, useState, useEffect } from 'react';
import Cropper, { ReactCropperElement } from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  CircularProgress,
  Divider,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import CloseIcon from '@mui/icons-material/Close';

interface PhotoCropDialogProps {
  open: boolean;
  imageSrc: string | null;
  onClose: () => void;
  onSave: (croppedBlob: Blob) => Promise<void> | void;
  isSaving?: boolean;
}

const PhotoCropDialog: FC<PhotoCropDialogProps> = ({
  open,
  imageSrc,
  onClose,
  onSave,
  isSaving = false,
}) => {
  const cropperRef = useRef<ReactCropperElement>(null);
  const [aspectRatio, setAspectRatio] = useState<number | undefined>(undefined);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);

  const handleCropperReady = () => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      cropper.reset();
    }
  };

  useEffect(() => {
    if (open) {
      setAspectRatio(undefined);
      setFlipH(false);
      setFlipV(false);
      const timer = setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
        cropperRef.current?.cropper?.reset();
      }, 60);
      return () => clearTimeout(timer);
    }
  }, [open, imageSrc]);

  const handleRotateLeft = () => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      cropper.rotate(-90);
    }
  };

  const handleRotateRight = () => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      cropper.rotate(90);
    }
  };

  const handleFlipHorizontal = () => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      const newFlip = !flipH;
      setFlipH(newFlip);
      cropper.scaleX(newFlip ? -1 : 1);
    }
  };

  const handleFlipVertical = () => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      const newFlip = !flipV;
      setFlipV(newFlip);
      cropper.scaleY(newFlip ? -1 : 1);
    }
  };

  const handleReset = () => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      cropper.reset();
      setAspectRatio(undefined);
      setFlipH(false);
      setFlipV(false);
      cropper.scale(1, 1);
    }
  };

  const handleAspectRatioChange = (ratioVal: string) => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;

    if (ratioVal === 'free') {
      setAspectRatio(undefined);
      cropper.setAspectRatio(NaN);
    } else {
      const numRatio = Number(ratioVal);
      setAspectRatio(numRatio);
      cropper.setAspectRatio(numRatio);
    }
  };

  const handleDone = () => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;

    const canvas = cropper.getCroppedCanvas({
      maxWidth: 2048,
      maxHeight: 2048,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
    });

    if (!canvas) return;

    canvas.toBlob(
      (blob) => {
        if (blob) {
          onSave(blob);
        }
      },
      'image/jpeg',
      0.95
    );
  };

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      maxWidth={false}
      transitionDuration={{ enter: 100, exit: 100 }}
      TransitionProps={{
        onEntered: () => {
          window.dispatchEvent(new Event('resize'));
          cropperRef.current?.cropper?.reset();
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: '96vw', sm: '90vw', md: '920px' },
          maxWidth: '920px',
          maxHeight: { xs: '92dvh', sm: '90vh', md: '90vh' },
          borderRadius: '14px',
          overflow: 'hidden',
          backgroundColor: '#ffffff',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          m: { xs: '8px auto', sm: 2 },
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Mobile top header bar */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          alignItems: 'center',
          justifyContent: 'space-between',
          px: '14px',
          py: '10px',
          borderBottom: '1px solid #e4e4e7',
          backgroundColor: '#ffffff',
          flexShrink: 0,
          zIndex: 10,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '16px',
            color: '#09090b',
            lineHeight: 1.2,
          }}
        >
          Кадрирование
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Button
            onClick={handleReset}
            size="small"
            variant="outlined"
            startIcon={<RestartAltIcon sx={{ fontSize: '14px !important' }} />}
            sx={{
              textTransform: 'none',
              fontSize: '12px',
              fontWeight: 500,
              color: '#09090b',
              borderColor: '#e4e4e7',
              borderRadius: '6px',
              py: '2px',
              px: '8px',
              minWidth: 'auto',
              '&:hover': {
                backgroundColor: '#f4f4f5',
                borderColor: '#d4d4d8',
              },
            }}
          >
            Сброс
          </Button>

          <IconButton
            onClick={onClose}
            disabled={isSaving}
            size="small"
            sx={{
              color: '#71717a',
              p: '4px',
              '&:hover': {
                backgroundColor: '#f4f4f5',
                color: '#09090b',
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Desktop top right close button */}
      <Tooltip title="Закрыть">
        <IconButton
          onClick={onClose}
          disabled={isSaving}
          size="small"
          sx={{
            display: { xs: 'none', md: 'flex' },
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 10,
            color: '#71717a',
            backgroundColor: 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(4px)',
            border: '1px solid #e4e4e7',
            '&:hover': {
              backgroundColor: '#f4f4f5',
              color: '#09090b',
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      <DialogContent
        sx={{
          p: 0,
          overflowY: { xs: 'auto', md: 'hidden' },
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            flex: 1,
            minHeight: { xs: 'auto', md: '560px' },
          }}
        >
          {/* Left area: Cropper Viewport with dark checkerboard background */}
          <Box
            sx={{
              width: '100%',
              height: { xs: '280px', sm: '340px', md: '560px' },
              minHeight: { xs: '280px', sm: '340px', md: '560px' },
              maxHeight: { xs: '280px', sm: '340px', md: '560px' },
              flex: { xs: 'none', md: 1 },
              flexShrink: 0,
              position: 'relative',
              backgroundColor: '#27272a',
              backgroundImage: `
                linear-gradient(45deg, #1f1f23 25%, transparent 25%),
                linear-gradient(-45deg, #1f1f23 25%, transparent 25%),
                linear-gradient(45deg, transparent 75%, #1f1f23 75%),
                linear-gradient(-45deg, transparent 75%, #1f1f23 75%)
              `,
              backgroundSize: '20px 20px',
              backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
              overflow: 'hidden',
              display: 'block',
              '& .cropper-container': {
                width: '100% !important',
                height: '100% !important',
              },
              /* Photocn theme styling overrides for cropperjs */
              '& .cropper-point': {
                width: '14px !important',
                height: '14px !important',
                backgroundColor: '#ffffff !important',
                borderRadius: '50% !important',
                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.45) !important',
                opacity: '1 !important',
              },
              '& .cropper-point.point-n, & .cropper-point.point-s': {
                marginLeft: '-7px !important',
              },
              '& .cropper-point.point-e, & .cropper-point.point-w': {
                marginTop: '-7px !important',
              },
              '& .cropper-point.point-ne': {
                marginTop: '-7px !important',
                marginRight: '-7px !important',
              },
              '& .cropper-point.point-nw': {
                marginTop: '-7px !important',
                marginLeft: '-7px !important',
              },
              '& .cropper-point.point-se': {
                marginBottom: '-7px !important',
                marginRight: '-7px !important',
              },
              '& .cropper-point.point-sw': {
                marginBottom: '-7px !important',
                marginLeft: '-7px !important',
              },
              '& .cropper-view-box': {
                outline: '1.5px solid rgba(255, 255, 255, 0.95) !important',
              },
              '& .cropper-line': {
                backgroundColor: 'transparent !important',
              },
              '& .cropper-dashed': {
                borderColor: 'rgba(255, 255, 255, 0.35) !important',
              },
              '& .cropper-modal': {
                backgroundColor: 'rgba(0, 0, 0, 0.55) !important',
              },
            }}
          >
            {imageSrc ? (
              <Cropper
                ref={cropperRef}
                src={imageSrc}
                style={{ height: '100%', width: '100%' }}
                initialAspectRatio={NaN}
                guides={true}
                viewMode={1}
                dragMode="move"
                scalable={true}
                cropBoxMovable={true}
                cropBoxResizable={true}
                responsive={true}
                autoCropArea={0.88}
                checkOrientation={false}
                background={false}
                checkCrossOrigin={false}
                ready={handleCropperReady}
              />
            ) : (
              <Typography sx={{ color: '#a1a1aa' }}>Изображение не выбрано</Typography>
            )}
          </Box>

          {/* Right sidebar: Photocn controls */}
          <Box
            sx={{
              width: { xs: '100%', md: '280px' },
              flexShrink: 0,
              backgroundColor: '#ffffff',
              borderLeft: { xs: 'none', md: '1px solid #e4e4e7' },
              borderTop: { xs: '1px solid #e4e4e7', md: 'none' },
              p: { xs: '14px 16px', md: '24px 20px' },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              boxSizing: 'border-box',
              flex: { xs: 'none', md: 1 },
            }}
          >
            {/* Top controls group */}
            <Box>
              {/* Header: Title + Subtitle + Reset button (Desktop only, mobile has top bar) */}
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  pr: 4, // leave room for close icon on desktop
                }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      fontSize: '20px',
                      color: '#09090b',
                      lineHeight: 1.2,
                    }}
                  >
                    Кадрирование
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '13px',
                      color: '#71717a',
                      mt: '3px',
                      maxWidth: '150px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    Перетащите рамку для обрезки...
                  </Typography>
                </Box>

                {/* Reset button */}
                <Button
                  onClick={handleReset}
                  size="small"
                  variant="outlined"
                  startIcon={<RestartAltIcon sx={{ fontSize: '15px !important' }} />}
                  sx={{
                    textTransform: 'none',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#09090b',
                    borderColor: '#e4e4e7',
                    borderRadius: '6px',
                    py: '3px',
                    px: '8px',
                    minWidth: 'auto',
                    '&:hover': {
                      backgroundColor: '#f4f4f5',
                      borderColor: '#d4d4d8',
                    },
                  }}
                >
                  Сброс
                </Button>
              </Box>

              <Divider sx={{ display: { xs: 'none', md: 'block' }, my: '18px', borderColor: '#f4f4f5' }} />

              {/* Action buttons row: rotate left, rotate right, flip h, flip v */}
              <Box sx={{ display: 'flex', gap: '8px', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                <Tooltip title="Повернуть влево (-90°)" placement="top" disableInteractive disableFocusListener arrow>
                  <IconButton
                    onClick={handleRotateLeft}
                    sx={{
                      width: 36,
                      height: 36,
                      border: '1px solid #e4e4e7',
                      borderRadius: '6px',
                      color: '#09090b',
                      '&:hover': {
                        backgroundColor: '#f4f4f5',
                      },
                    }}
                  >
                    <RotateLeftIcon sx={{ fontSize: 19, pointerEvents: 'none' }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Повернуть вправо (+90°)" placement="top" disableInteractive disableFocusListener arrow>
                  <IconButton
                    onClick={handleRotateRight}
                    sx={{
                      width: 36,
                      height: 36,
                      border: '1px solid #e4e4e7',
                      borderRadius: '6px',
                      color: '#09090b',
                      '&:hover': {
                        backgroundColor: '#f4f4f5',
                      },
                    }}
                  >
                    <RotateRightIcon sx={{ fontSize: 19, pointerEvents: 'none' }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Отразить по горизонтали" placement="top" disableInteractive disableFocusListener arrow>
                  <IconButton
                    onClick={handleFlipHorizontal}
                    sx={{
                      width: 36,
                      height: 36,
                      border: '1px solid #e4e4e7',
                      borderRadius: '6px',
                      color: flipH ? '#740BB6' : '#09090b',
                      backgroundColor: flipH ? '#f3e8ff' : 'transparent',
                      '&:hover': {
                        backgroundColor: flipH ? '#ede9fe' : '#f4f4f5',
                      },
                    }}
                  >
                    <SwapHorizIcon sx={{ fontSize: 19, pointerEvents: 'none' }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Отразить по вертикали" placement="top" disableInteractive disableFocusListener arrow>
                  <IconButton
                    onClick={handleFlipVertical}
                    sx={{
                      width: 36,
                      height: 36,
                      border: '1px solid #e4e4e7',
                      borderRadius: '6px',
                      color: flipV ? '#740BB6' : '#09090b',
                      backgroundColor: flipV ? '#f3e8ff' : 'transparent',
                      '&:hover': {
                        backgroundColor: flipV ? '#ede9fe' : '#f4f4f5',
                      },
                    }}
                  >
                    <SwapVertIcon sx={{ fontSize: 19, pointerEvents: 'none' }} />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Aspect Ratio section */}
              <Box sx={{ mt: { xs: '12px', md: '20px' } }}>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#09090b',
                    mb: '6px',
                  }}
                >
                  Соотношение сторон
                </Typography>
                <Select
                  value={aspectRatio === undefined ? 'free' : String(aspectRatio)}
                  onChange={(e) => handleAspectRatioChange(e.target.value)}
                  size="small"
                  fullWidth
                  sx={{
                    borderRadius: '6px',
                    fontSize: '14px',
                    color: '#09090b',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e4e4e7',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#d4d4d8',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#09090b',
                      borderWidth: '1.5px',
                    },
                  }}
                >
                  <MenuItem value="free">Произвольное</MenuItem>
                  <MenuItem value={String(1)}>1:1 (Квадрат)</MenuItem>
                  <MenuItem value={String(4 / 3)}>4:3 (Стандарт)</MenuItem>
                  <MenuItem value={String(16 / 9)}>16:9 (Широкий)</MenuItem>
                  <MenuItem value={String(3 / 4)}>3:4 (Портрет)</MenuItem>
                </Select>
              </Box>
            </Box>

            {/* Bottom: Done button */}
            <Box
              sx={{
                mt: { xs: '14px', md: '30px' },
                position: { xs: 'sticky', md: 'static' },
                bottom: 0,
                backgroundColor: '#ffffff',
                pt: { xs: '6px', md: 0 },
                pb: { xs: '4px', md: 0 },
                zIndex: 5,
              }}
            >
              <Button
                onClick={handleDone}
                disabled={isSaving}
                fullWidth
                sx={{
                  backgroundColor: '#09090b',
                  color: '#ffffff',
                  height: '42px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#27272a',
                    boxShadow: 'none',
                  },
                  '&:disabled': {
                    backgroundColor: '#71717a',
                    color: '#e4e4e7',
                  },
                }}
              >
                {isSaving ? (
                  <CircularProgress size={20} sx={{ color: '#ffffff' }} />
                ) : (
                  'Готово'
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PhotoCropDialog;
